import type { Job } from "../types/Job";

/** What the box's text turned out to be. `text` is anything for the search to answer. */
export type JobReference =
	| { kind: "id"; id: string }
	| { kind: "name"; name: string }
	| { kind: "text"; text: string }
	| { kind: "invalid"; reason: string };

/** An Airtable record id: `rec` and alphanumerics — exactly fourteen of them from Airtable, a
 *  few more from the offline seed, so the floor is what is checked. */
const RECORD_ID = /^rec[A-Za-z0-9]{14,}$/;
/** Any airtable.com link. When it carries a record, that is the path segment starting with
 *  `rec`, as in https://airtable.com/appX/tblY/viwZ/recW?blocks=hide. */
const AIRTABLE_URL = /^https?:\/\/(?:[\w-]+\.)?airtable\.com\//i;
const RECORD_ID_IN_URL = /\/(rec[A-Za-z0-9]{14,})(?:[/?#]|$)/;
/** What joins a job name's parts in Airtable's `Name` formula, so every job name holds it. */
const JOB_NAME_SEPARATOR = "__";

/** Reads the box's text: a bare record id, an Airtable link carrying one, a job's name as Airtable
 *  shows it, or failing those, text to search for. Nothing at all is invalid. */
export function parseJobReference(raw: string): JobReference {
	const text = raw.trim();
	if (!text) return { kind: "invalid", reason: "Paste a record ID, an Airtable link, or the job's name." };
	if (RECORD_ID.test(text)) return { kind: "id", id: text };
	if (AIRTABLE_URL.test(text)) {
		const match = RECORD_ID_IN_URL.exec(text);
		return match
			? { kind: "id", id: match[1] }
			: {
					kind: "invalid",
					reason: "That Airtable link has no record in it — open the job and copy the link from there.",
				};
	}
	return text.includes(JOB_NAME_SEPARATOR) ? { kind: "name", name: text } : { kind: "text", text };
}

/** Below this, nearly every job matches. */
const MIN_QUERY_LENGTH = 2;
/** The search endpoint refuses anything longer. */
const MAX_QUERY_LENGTH = 200;

/** The text worth sending to the job search, or null. A name is, for the rows it offers while it is
 *  still being typed. An id or link goes straight to its job, and text outside the endpoint's bounds
 *  has nothing useful to ask. */
export function searchableQuery(raw: string): string | null {
	const { kind } = parseJobReference(raw);
	if (kind === "id" || kind === "invalid") return null;
	const text = raw.trim();
	return text.length >= MIN_QUERY_LENGTH && text.length <= MAX_QUERY_LENGTH ? text : null;
}

/** The jobs whose name is the given one, compared the way Airtable compares text: the whole
 *  string, case-insensitive. Surrounding whitespace is the paste's, not the name's. */
export function jobsNamed<T extends Pick<Job, "name">>(jobs: readonly T[], name: string): T[] {
	const wanted = name.trim().toLowerCase();
	return jobs.filter((job) => (job.name ?? "").trim().toLowerCase() === wanted);
}
